import clsx4 from 'clsx';
import { twMerge } from 'tailwind-merge';
import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import React, { forwardRef, useState, createContext, isValidElement, Children, Fragment as Fragment$1, useContext, useMemo, useEffect, useRef, useCallback } from 'react';
import { isNil, newId, isEmptyArray } from '@etudescarre/utils';
import { Checkbox as Checkbox$1 } from '@ark-ui/react/checkbox';
import { CheckIcon, MinusIcon, CaretUpIcon, CaretDownIcon, CaretUpDownIcon, CaretDown, CaretRight, CaretLeftIcon, CaretRightIcon, Info, CheckCircle, Warning, WarningCircle, X } from '@phosphor-icons/react';
import { useListCollection, Combobox as Combobox$1 } from '@ark-ui/react/combobox';
import { useFilter } from '@ark-ui/react/locale';
import { Portal } from '@ark-ui/react/portal';
import { Dialog } from '@ark-ui/react/dialog';
import { useReactTable, getCoreRowModel, flexRender, functionalUpdate } from '@tanstack/react-table';
import { DatePicker as DatePicker$1, parseDate } from '@ark-ui/react/date-picker';
import { Menu as Menu$1 } from '@ark-ui/react/menu';
import { Pagination as Pagination$1 } from '@ark-ui/react/pagination';
import { createListCollection, Select as Select$1 } from '@ark-ui/react/select';
import { Steps as Steps$1 } from '@ark-ui/react/steps';
import { Tabs as Tabs$1 } from '@ark-ui/react/tabs';
import { Toast as Toast$1 } from '@ark-ui/react/toast';
export { Toaster as ToastProvider, createToaster } from '@ark-ui/react/toast';

// src/utils.ts
var cn = (...classes) => twMerge(clsx4(classes));
var clsx = cn;
function Badge({
  children,
  variant = "default",
  size = "sm",
  className
}) {
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: cn(
        "inline-flex items-center rounded-full font-medium",
        // Size variants
        size === "sm" && "px-2 py-0.5 text-xs",
        size === "md" && "px-3 py-1 text-sm",
        // Color variants
        variant === "default" && "bg-gray-100 text-gray-800",
        variant === "blue" && "bg-blue-100 text-blue-800",
        variant === "green" && "bg-green-100 text-green-800",
        variant === "yellow" && "bg-yellow-100 text-yellow-800",
        variant === "red" && "bg-red-100 text-red-800",
        variant === "purple" && "bg-purple-100 text-purple-800",
        variant === "pink" && "bg-pink-100 text-pink-800",
        variant === "brand" && "bg-brand-100 text-brand",
        className
      ),
      children
    }
  );
}
function assignRef(ref, value) {
  if (isNil(ref)) {
    return;
  }
  if (typeof ref === "function") {
    ref(value);
    return;
  }
}
function mergeRefs(...refs) {
  return (node) => {
    for (const ref of refs) {
      assignRef(ref, node);
    }
  };
}
function useMergeRefs(...refs) {
  return useMemo(() => mergeRefs(...refs), refs);
}
var HANDLE_FN_PATTERN = /^on[A-Z]/;
var Slot = React.forwardRef((props, forwardedRef) => {
  const { children, ...slotProps } = props;
  const childrenArray = React.Children.toArray(children);
  const slottable = childrenArray.find(isSlottable);
  if (slottable) {
    const newElement = slottable.props.children;
    const newChildren = childrenArray.map((child) => {
      if (child === slottable) {
        if (React.Children.count(newElement) > 1) {
          return React.Children.only(null);
        }
        return React.isValidElement(newElement) ? newElement.props.children : null;
      }
      return child;
    });
    return /* @__PURE__ */ jsx(SlotClone, { ...slotProps, ref: forwardedRef, children: React.isValidElement(newElement) ? React.cloneElement(newElement, void 0, newChildren) : null });
  }
  return /* @__PURE__ */ jsx(SlotClone, { ...slotProps, ref: forwardedRef, children });
});
Slot.displayName = "Slot";
var SlotClone = React.forwardRef(
  (props, forwardedRef) => {
    const { children, ...slotProps } = props;
    if (React.isValidElement(children)) {
      const childrenRef = children.props.ref;
      return React.cloneElement(children, {
        ...mergeProps(slotProps, children.props),
        // @ts-expect-error
        ref: forwardedRef ? mergeRefs(forwardedRef, childrenRef) : childrenRef
      });
    }
    return React.Children.count(children) > 1 ? React.Children.only(null) : null;
  }
);
SlotClone.displayName = "SlotClone";
var Slottable = ({ children }) => {
  return /* @__PURE__ */ jsx(Fragment, { children });
};
function isSlottable(child) {
  return React.isValidElement(child) && child.type === Slottable;
}
function mergeProps(slotProps, childProps) {
  const overrideProps = { ...childProps };
  for (const propName in childProps) {
    if (Object.hasOwn(slotProps, propName) === false) {
      continue;
    }
    const slotPropValue = slotProps[propName];
    const childPropValue = childProps[propName];
    const isHandler = HANDLE_FN_PATTERN.test(propName);
    if (isHandler) {
      if (slotPropValue && childPropValue) {
        overrideProps[propName] = (...args) => {
          childPropValue(...args);
          slotPropValue(...args);
        };
      } else if (slotPropValue) {
        overrideProps[propName] = slotPropValue;
      }
    } else if (propName === "style") {
      overrideProps[propName] = {
        ...childPropValue,
        ...slotPropValue
      };
    } else if (propName === "className") {
      overrideProps[propName] = [childPropValue, slotPropValue].filter(Boolean).join(" ");
    }
  }
  return { ...slotProps, ...overrideProps };
}
var DataInteractive = forwardRef(
  ({
    disabled = false,
    autoFocus = false,
    children,
    onFocus,
    onBlur,
    onMouseEnter,
    onMouseLeave,
    onMouseDown,
    onMouseUp,
    ...props
  }, ref) => {
    const [isHovered, setIsHovered] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [isActive, setIsActive] = useState(false);
    const handleFocus = (e) => {
      setIsFocused(true);
      onFocus?.(e);
    };
    const handleBlur = (e) => {
      setIsFocused(false);
      onBlur?.(e);
    };
    const handleMouseEnter = (e) => {
      if (!disabled) {
        setIsHovered(true);
      }
      onMouseEnter?.(e);
    };
    const handleMouseLeave = (e) => {
      setIsHovered(false);
      setIsActive(false);
      onMouseLeave?.(e);
    };
    const handleMouseDown = (e) => {
      if (!disabled) {
        setIsActive(true);
      }
      onMouseDown?.(e);
    };
    const handleMouseUp = (e) => {
      setIsActive(false);
      onMouseUp?.(e);
    };
    return /* @__PURE__ */ jsx(
      Slot,
      {
        "data-active": !disabled && isActive || void 0,
        "data-autofocus": autoFocus || void 0,
        "data-disabled": disabled || void 0,
        "data-focus": !disabled && isFocused || void 0,
        "data-hover": !disabled && isHovered || void 0,
        onBlur: handleBlur,
        onFocus: handleFocus,
        onMouseDown: handleMouseDown,
        onMouseEnter: handleMouseEnter,
        onMouseLeave: handleMouseLeave,
        onMouseUp: handleMouseUp,
        ref,
        ...props,
        children
      }
    );
  }
);
DataInteractive.displayName = "DataInteractive";
var Link = forwardRef(function Link2(props, ref) {
  return /* @__PURE__ */ jsx(DataInteractive, { children: /* @__PURE__ */ jsx("a", { ...props, ref }) });
});
var styles = {
  base: [
    // Base
    "relative isolate inline-flex items-baseline justify-center gap-x-2 rounded-lg border text-base/6 font-semibold",
    // Sizing
    "px-[calc(--spacing(3.5)-1px)] py-[calc(--spacing(2.5)-1px)] sm:px-[calc(--spacing(3)-1px)] sm:py-[calc(--spacing(1.5)-1px)] sm:text-sm/6",
    // Focus
    "focus:not-data-focus:outline-hidden data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-blue-500",
    // Disabled
    "data-disabled:opacity-50 data-disabled:cursor-not-allowed",
    // Icon
    "*:data-[slot=icon]:-mx-0.5 *:data-[slot=icon]:my-0.5 *:data-[slot=icon]:size-5 *:data-[slot=icon]:shrink-0 *:data-[slot=icon]:self-center *:data-[slot=icon]:text-(--btn-icon) sm:*:data-[slot=icon]:my-1 sm:*:data-[slot=icon]:size-4 forced-colors:[--btn-icon:ButtonText] forced-colors:data-hover:[--btn-icon:ButtonText]",
    "cursor-pointer"
  ],
  solid: [
    // Optical border, implemented as the button background to avoid corner artifacts
    "border-transparent bg-(--btn-border)",
    // Dark mode: border is rendered on `after` so background is set to button background
    "dark:bg-(--btn-bg)",
    // Button background, implemented as foreground layer to stack on top of pseudo-border layer
    "before:absolute before:inset-0 before:-z-10 before:rounded-[calc(var(--radius-lg)-1px)] before:bg-(--btn-bg)",
    // Drop shadow, applied to the inset `before` layer so it blends with the border
    "before:shadow-sm",
    // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
    "dark:before:hidden",
    // Dark mode: Subtle white outline is applied using a border
    "dark:border-white/5",
    // Shim/overlay, inset to match button foreground and used for hover state + highlight shadow
    "after:absolute after:inset-0 after:-z-10 after:rounded-[calc(var(--radius-lg)-1px)]",
    // Inner highlight shadow
    "after:shadow-[inset_0_1px_--theme(--color-white/15%)]",
    // White overlay on hover
    "data-active:after:bg-(--btn-hover-overlay) data-hover:after:bg-(--btn-hover-overlay)",
    // Dark mode: `after` layer expands to cover entire button
    "dark:after:-inset-px dark:after:rounded-lg",
    // Disabled
    "data-disabled:before:shadow-none data-disabled:after:shadow-none"
  ],
  outline: [
    // Base
    "border-zinc-950/10 text-zinc-950 data-active:bg-zinc-950/2.5 data-hover:bg-zinc-950/2.5",
    // Dark mode
    "dark:border-white/15 dark:text-white dark:[--btn-bg:transparent] dark:data-active:bg-white/5 dark:data-hover:bg-white/5",
    // Focus
    "focus:outline-none data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-blue-500",
    // Disabled
    "data-disabled:cursor-not-allowed",
    // Icon
    "[--btn-icon:var(--color-zinc-500)] data-active:[--btn-icon:var(--color-zinc-700)] data-hover:[--btn-icon:var(--color-zinc-700)] dark:data-active:[--btn-icon:var(--color-zinc-400)] dark:data-hover:[--btn-icon:var(--color-zinc-400)]"
  ],
  plain: [
    // Base
    "border-transparent text-zinc-950 data-active:bg-zinc-950/5 data-hover:bg-zinc-950/5",
    // Dark mode
    "dark:text-white dark:data-active:bg-white/10 dark:data-hover:bg-white/10",
    // Icon
    "[--btn-icon:var(--color-zinc-500)] data-active:[--btn-icon:var(--color-zinc-700)] data-hover:[--btn-icon:var(--color-zinc-700)] dark:[--btn-icon:var(--color-zinc-500)] dark:data-active:[--btn-icon:var(--color-zinc-400)] dark:data-hover:[--btn-icon:var(--color-zinc-400)]"
  ],
  colors: {
    "dark/zinc": [
      "text-white [--btn-bg:var(--color-zinc-900)] [--btn-border:var(--color-zinc-950)]/90 [--btn-hover-overlay:var(--color-white)]/10",
      "dark:text-white dark:[--btn-bg:var(--color-zinc-600)] dark:[--btn-hover-overlay:var(--color-white)]/5",
      "[--btn-icon:var(--color-zinc-400)] data-active:[--btn-icon:var(--color-zinc-300)] data-hover:[--btn-icon:var(--color-zinc-300)]"
    ],
    light: [
      "text-zinc-950 [--btn-bg:white] [--btn-border:var(--color-zinc-950)]/10 [--btn-hover-overlay:var(--color-zinc-950)]/2.5 data-active:[--btn-border:var(--color-zinc-950)]/15 data-hover:[--btn-border:var(--color-zinc-950)]/15",
      "dark:text-white dark:[--btn-hover-overlay:var(--color-white)]/5 dark:[--btn-bg:var(--color-zinc-800)]",
      "[--btn-icon:var(--color-zinc-500)] data-active:[--btn-icon:var(--color-zinc-700)] data-hover:[--btn-icon:var(--color-zinc-700)] dark:[--btn-icon:var(--color-zinc-500)] dark:data-active:[--btn-icon:var(--color-zinc-400)] dark:data-hover:[--btn-icon:var(--color-zinc-400)]"
    ],
    "dark/white": [
      "text-white [--btn-bg:var(--color-zinc-900)] [--btn-border:var(--color-zinc-950)]/90 [--btn-hover-overlay:var(--color-white)]/10",
      "dark:text-zinc-950 dark:[--btn-bg:white] dark:[--btn-hover-overlay:var(--color-zinc-950)]/5",
      "[--btn-icon:var(--color-zinc-400)] data-active:[--btn-icon:var(--color-zinc-300)] data-hover:[--btn-icon:var(--color-zinc-300)] dark:[--btn-icon:var(--color-zinc-500)] dark:data-active:[--btn-icon:var(--color-zinc-400)] dark:data-hover:[--btn-icon:var(--color-zinc-400)]"
    ],
    dark: [
      "text-white [--btn-bg:var(--color-zinc-900)] [--btn-border:var(--color-zinc-950)]/90 [--btn-hover-overlay:var(--color-white)]/10",
      "dark:[--btn-hover-overlay:var(--color-white)]/5 dark:[--btn-bg:var(--color-zinc-800)]",
      "[--btn-icon:var(--color-zinc-400)] data-active:[--btn-icon:var(--color-zinc-300)] data-hover:[--btn-icon:var(--color-zinc-300)]"
    ],
    white: [
      "text-zinc-950 [--btn-bg:white] [--btn-border:var(--color-zinc-950)]/10 [--btn-hover-overlay:var(--color-zinc-950)]/2.5 data-active:[--btn-border:var(--color-zinc-950)]/15 data-hover:[--btn-border:var(--color-zinc-950)]/15",
      "dark:[--btn-hover-overlay:var(--color-zinc-950)]/5",
      "[--btn-icon:var(--color-zinc-400)] data-active:[--btn-icon:var(--color-zinc-500)] data-hover:[--btn-icon:var(--color-zinc-500)]"
    ],
    zinc: [
      "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-zinc-600)] [--btn-border:var(--color-zinc-700)]/90",
      "dark:[--btn-hover-overlay:var(--color-white)]/5",
      "[--btn-icon:var(--color-zinc-400)] data-active:[--btn-icon:var(--color-zinc-300)] data-hover:[--btn-icon:var(--color-zinc-300)]"
    ],
    indigo: [
      "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-indigo-500)] [--btn-border:var(--color-indigo-600)]/90",
      "[--btn-icon:var(--color-indigo-300)] data-active:[--btn-icon:var(--color-indigo-200)] data-hover:[--btn-icon:var(--color-indigo-200)]"
    ],
    cyan: [
      "text-cyan-950 [--btn-bg:var(--color-cyan-300)] [--btn-border:var(--color-cyan-400)]/80 [--btn-hover-overlay:var(--color-white)]/25",
      "[--btn-icon:var(--color-cyan-500)]"
    ],
    red: [
      "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-red-600)] [--btn-border:var(--color-red-700)]/90",
      "[--btn-icon:var(--color-red-300)] data-active:[--btn-icon:var(--color-red-200)] data-hover:[--btn-icon:var(--color-red-200)]"
    ],
    orange: [
      "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-orange-500)] [--btn-border:var(--color-orange-600)]/90",
      "[--btn-icon:var(--color-orange-300)] data-active:[--btn-icon:var(--color-orange-200)] data-hover:[--btn-icon:var(--color-orange-200)]"
    ],
    amber: [
      "text-amber-950 [--btn-hover-overlay:var(--color-white)]/25 [--btn-bg:var(--color-amber-400)] [--btn-border:var(--color-amber-500)]/80",
      "[--btn-icon:var(--color-amber-600)]"
    ],
    yellow: [
      "text-yellow-950 [--btn-hover-overlay:var(--color-white)]/25 [--btn-bg:var(--color-yellow-300)] [--btn-border:var(--color-yellow-400)]/80",
      "[--btn-icon:var(--color-yellow-600)] data-active:[--btn-icon:var(--color-yellow-700)] data-hover:[--btn-icon:var(--color-yellow-700)]"
    ],
    lime: [
      "text-lime-950 [--btn-hover-overlay:var(--color-white)]/25 [--btn-bg:var(--color-lime-300)] [--btn-border:var(--color-lime-400)]/80",
      "[--btn-icon:var(--color-lime-600)] data-active:[--btn-icon:var(--color-lime-700)] data-hover:[--btn-icon:var(--color-lime-700)]"
    ],
    green: [
      "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-green-600)] [--btn-border:var(--color-green-700)]/90",
      "[--btn-icon:var(--color-white)]/60 data-active:[--btn-icon:var(--color-white)]/80 data-hover:[--btn-icon:var(--color-white)]/80"
    ],
    emerald: [
      "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-emerald-600)] [--btn-border:var(--color-emerald-700)]/90",
      "[--btn-icon:var(--color-white)]/60 data-active:[--btn-icon:var(--color-white)]/80 data-hover:[--btn-icon:var(--color-white)]/80"
    ],
    teal: [
      "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-teal-600)] [--btn-border:var(--color-teal-700)]/90",
      "[--btn-icon:var(--color-white)]/60 data-active:[--btn-icon:var(--color-white)]/80 data-hover:[--btn-icon:var(--color-white)]/80"
    ],
    sky: [
      "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-sky-500)] [--btn-border:var(--color-sky-600)]/80",
      "[--btn-icon:var(--color-white)]/60 data-active:[--btn-icon:var(--color-white)]/80 data-hover:[--btn-icon:var(--color-white)]/80"
    ],
    blue: [
      "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-blue-600)] [--btn-border:var(--color-blue-700)]/90",
      "[--btn-icon:var(--color-blue-400)] data-active:[--btn-icon:var(--color-blue-300)] data-hover:[--btn-icon:var(--color-blue-300)]"
    ],
    violet: [
      "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-violet-500)] [--btn-border:var(--color-violet-600)]/90",
      "[--btn-icon:var(--color-violet-300)] data-active:[--btn-icon:var(--color-violet-200)] data-hover:[--btn-icon:var(--color-violet-200)]"
    ],
    purple: [
      "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-purple-500)] [--btn-border:var(--color-purple-600)]/90",
      "[--btn-icon:var(--color-purple-300)] data-active:[--btn-icon:var(--color-purple-200)] data-hover:[--btn-icon:var(--color-purple-200)]"
    ],
    fuchsia: [
      "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-fuchsia-500)] [--btn-border:var(--color-fuchsia-600)]/90",
      "[--btn-icon:var(--color-fuchsia-300)] data-active:[--btn-icon:var(--color-fuchsia-200)] data-hover:[--btn-icon:var(--color-fuchsia-200)]"
    ],
    pink: [
      "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-pink-500)] [--btn-border:var(--color-pink-600)]/90",
      "[--btn-icon:var(--color-pink-300)] data-active:[--btn-icon:var(--color-pink-200)] data-hover:[--btn-icon:var(--color-pink-200)]"
    ],
    rose: [
      "text-white [--btn-hover-overlay:var(--color-white)]/10 [--btn-bg:var(--color-rose-500)] [--btn-border:var(--color-rose-600)]/90",
      "[--btn-icon:var(--color-rose-300)] data-active:[--btn-icon:var(--color-rose-200)] data-hover:[--btn-icon:var(--color-rose-200)]"
    ]
  }
};
var Button = forwardRef(function Button2({ color, outline, plain, className, children, ...props }, ref) {
  const classes = cn(
    className,
    styles.base,
    outline ? styles.outline : plain ? styles.plain : cn(styles.solid, styles.colors[color ?? "dark/zinc"])
  );
  return "href" in props ? /* @__PURE__ */ jsx(
    Link,
    {
      ...props,
      className: classes,
      ref,
      children: /* @__PURE__ */ jsx(TouchTarget, { children })
    }
  ) : /* @__PURE__ */ jsx(DataInteractive, { ...props, className: classes, children: /* @__PURE__ */ jsx(
    "button",
    {
      type: "type" in props ? props.type : "button",
      disabled: props.disabled,
      className: classes,
      ref,
      children: /* @__PURE__ */ jsx(TouchTarget, { children })
    }
  ) });
});
function TouchTarget({ children }) {
  return /* @__PURE__ */ jsxs(Fragment, { children: [
    /* @__PURE__ */ jsx(
      "span",
      {
        "aria-hidden": "true",
        className: "-translate-x-1/2 -translate-y-1/2 absolute top-1/2 left-1/2 pointer-fine:hidden size-[max(100%,2.75rem)]"
      }
    ),
    children
  ] });
}
var Checkbox = forwardRef(
  ({ className, children, ...props }, ref) => {
    return /* @__PURE__ */ jsxs(
      Checkbox$1.Root,
      {
        ref,
        className: clsx4("inline-flex items-center gap-2", className),
        ...props,
        children: [
          /* @__PURE__ */ jsxs(Checkbox$1.Control, { className: "flex size-4 items-center justify-center rounded border border-zinc-950/10 bg-white transition-colors data-[state=checked]:border-blue-600 data-[state=checked]:bg-blue-600 data-[state=indeterminate]:border-blue-600 data-[state=indeterminate]:bg-blue-600 data-disabled:cursor-not-allowed data-disabled:opacity-50 data-invalid:border-red-600 dark:border-white/15 dark:bg-zinc-900 dark:data-[state=checked]:border-blue-500 dark:data-[state=checked]:bg-blue-500 dark:data-[state=indeterminate]:border-blue-500 dark:data-[state=indeterminate]:bg-blue-500", children: [
            /* @__PURE__ */ jsx(Checkbox$1.Indicator, { children: /* @__PURE__ */ jsx(CheckIcon, { weight: "bold", className: "size-3 text-white" }) }),
            /* @__PURE__ */ jsx(Checkbox$1.Indicator, { indeterminate: true, children: /* @__PURE__ */ jsx(MinusIcon, { weight: "bold", className: "size-3 text-white" }) })
          ] }),
          children && /* @__PURE__ */ jsx(Checkbox$1.Label, { className: "select-none text-sm font-medium leading-none text-zinc-950 data-disabled:cursor-not-allowed data-disabled:opacity-50 dark:text-white", children }),
          /* @__PURE__ */ jsx(Checkbox$1.HiddenInput, {})
        ]
      }
    );
  }
);
Checkbox.displayName = "Checkbox";
function Combobox({
  options,
  value,
  defaultValue,
  onValueChange,
  placeholder = "Search...",
  disabled = false,
  multiple = false,
  label,
  groupBy,
  className
}) {
  const { contains } = useFilter({ sensitivity: "base" });
  const { collection, filter, set } = useListCollection({
    initialItems: options,
    filter: contains,
    itemToString: (item) => item.label,
    itemToValue: (item) => item.value,
    ...groupBy && { groupBy }
  });
  useEffect(() => {
    set(options);
  }, [options, set]);
  const handleInputChange = (details) => {
    filter(details.inputValue);
  };
  const handleValueChange = (details) => {
    if (onValueChange) {
      const values = details.items.map((item) => item.value);
      onValueChange({ value: values });
    }
  };
  return /* @__PURE__ */ jsxs(
    Combobox$1.Root,
    {
      collection,
      onInputValueChange: handleInputChange,
      onValueChange: handleValueChange,
      disabled,
      multiple,
      defaultValue,
      value,
      className: clsx4("relative w-full", className),
      children: [
        label && /* @__PURE__ */ jsx(Combobox$1.Label, { className: "block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1", children: label }),
        /* @__PURE__ */ jsxs(Combobox$1.Control, { className: "relative", children: [
          /* @__PURE__ */ jsx(
            Combobox$1.Input,
            {
              placeholder,
              className: clsx4(
                "w-full rounded-lg border border-zinc-300 dark:border-zinc-700",
                "bg-white dark:bg-zinc-900",
                "px-3 py-2 pr-10",
                "text-sm text-zinc-900 dark:text-zinc-100",
                "placeholder:text-zinc-500 dark:placeholder:text-zinc-400",
                "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                "disabled:bg-zinc-100 dark:disabled:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50"
              )
            }
          ),
          /* @__PURE__ */ jsx(
            Combobox$1.Trigger,
            {
              className: clsx4(
                "absolute right-2 top-1/2 -translate-y-1/2",
                "text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200",
                "disabled:opacity-50"
              ),
              children: /* @__PURE__ */ jsxs(
                "svg",
                {
                  xmlns: "http://www.w3.org/2000/svg",
                  width: "20",
                  height: "20",
                  viewBox: "0 0 256 256",
                  fill: "currentColor",
                  role: "img",
                  "aria-label": "Toggle dropdown",
                  children: [
                    /* @__PURE__ */ jsx("title", { children: "Toggle dropdown" }),
                    /* @__PURE__ */ jsx("path", { d: "M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,53.66,90.34L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z" })
                  ]
                }
              )
            }
          ),
          /* @__PURE__ */ jsx(
            Combobox$1.ClearTrigger,
            {
              className: clsx4(
                "absolute right-9 top-1/2 -translate-y-1/2",
                "text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200",
                "hidden data-[value]:block"
              ),
              children: /* @__PURE__ */ jsxs(
                "svg",
                {
                  xmlns: "http://www.w3.org/2000/svg",
                  width: "16",
                  height: "16",
                  viewBox: "0 0 256 256",
                  fill: "currentColor",
                  role: "img",
                  "aria-label": "Clear selection",
                  children: [
                    /* @__PURE__ */ jsx("title", { children: "Clear selection" }),
                    /* @__PURE__ */ jsx("path", { d: "M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z" })
                  ]
                }
              )
            }
          )
        ] }),
        /* @__PURE__ */ jsx(Portal, { children: /* @__PURE__ */ jsx(Combobox$1.Positioner, { className: "!w-[var(--reference-width)]", children: /* @__PURE__ */ jsxs(
          Combobox$1.Content,
          {
            className: clsx4(
              "mt-1 w-full rounded-lg border border-zinc-300 dark:border-zinc-700",
              "bg-white dark:bg-zinc-900",
              "shadow-lg",
              "max-h-60 overflow-auto",
              "z-[60]",
              "data-[state=open]:animate-appear data-[state=closed]:animate-disappear"
            ),
            children: [
              /* @__PURE__ */ jsx(Combobox$1.List, { children: groupBy ? collection.group().map(([groupName, groupItems]) => /* @__PURE__ */ jsxs(Combobox$1.ItemGroup, { children: [
                /* @__PURE__ */ jsx(
                  Combobox$1.ItemGroupLabel,
                  {
                    className: clsx4(
                      "px-3 py-2 text-xs font-semibold text-zinc-500 dark:text-zinc-400",
                      "bg-zinc-50 dark:bg-zinc-800",
                      "sticky top-0"
                    ),
                    children: groupName
                  }
                ),
                groupItems.map((item) => /* @__PURE__ */ jsxs(
                  Combobox$1.Item,
                  {
                    item,
                    className: clsx4(
                      "relative flex items-center justify-between",
                      "px-3 py-2 text-sm",
                      "cursor-pointer select-none",
                      "text-zinc-900 dark:text-zinc-100",
                      "data-[highlighted]:bg-blue-50 dark:data-[highlighted]:bg-blue-900/20",
                      "data-[state=checked]:bg-blue-100 dark:data-[state=checked]:bg-blue-900/30"
                    ),
                    children: [
                      /* @__PURE__ */ jsx(Combobox$1.ItemText, { children: item.label }),
                      /* @__PURE__ */ jsx(Combobox$1.ItemIndicator, { className: "text-blue-600 dark:text-blue-400", children: "\u2713" })
                    ]
                  },
                  item.value
                ))
              ] }, groupName)) : collection.items.map((item) => /* @__PURE__ */ jsxs(
                Combobox$1.Item,
                {
                  item,
                  className: clsx4(
                    "relative flex items-center justify-between",
                    "px-3 py-2 text-sm",
                    "cursor-pointer select-none",
                    "text-zinc-900 dark:text-zinc-100",
                    "data-[highlighted]:bg-blue-50 dark:data-[highlighted]:bg-blue-900/20",
                    "data-[state=checked]:bg-blue-100 dark:data-[state=checked]:bg-blue-900/30"
                  ),
                  children: [
                    /* @__PURE__ */ jsx(Combobox$1.ItemText, { children: item.label }),
                    /* @__PURE__ */ jsx(Combobox$1.ItemIndicator, { className: "text-blue-600 dark:text-blue-400", children: "\u2713" })
                  ]
                },
                item.value
              )) }),
              collection.items.length === 0 && /* @__PURE__ */ jsx(Combobox$1.Empty, { className: "px-3 py-8 text-center text-sm text-zinc-500 dark:text-zinc-400", children: "Aucun r\xE9sultat trouv\xE9" })
            ]
          }
        ) }) })
      ]
    }
  );
}
Combobox.displayName = "Combobox";
var sizeClasses = {
  sm: "max-w-sm",
  md: "max-w-md",
  lg: "max-w-lg",
  xl: "max-w-xl",
  "2xl": "max-w-2xl",
  full: "max-w-full mx-4"
};
var Root2 = ({ children, ...props }) => {
  return /* @__PURE__ */ jsx(
    Dialog.Root,
    {
      closeOnEscape: false,
      closeOnInteractOutside: false,
      onInteractOutside: (event) => event.preventDefault(),
      ...props,
      children
    }
  );
};
Root2.displayName = "Modal.Root";
var Trigger = ({
  children,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    Dialog.Trigger,
    {
      className: clsx4(
        "inline-flex items-center gap-x-2 rounded-lg border px-3.5 py-2.5 text-base/6 font-semibold sm:px-3 sm:py-1.5 sm:text-sm/6",
        "border-zinc-950/10 text-zinc-950 data-active:bg-zinc-950/2.5 data-hover:bg-zinc-950/2.5",
        "dark:border-white/15 dark:text-white dark:data-active:bg-white/5 dark:data-hover:bg-white/5",
        "focus:outline-none data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-blue-500",
        "data-disabled:opacity-50 data-disabled:cursor-not-allowed",
        className
      ),
      ...props,
      children
    }
  );
};
Trigger.displayName = "Modal.Trigger";
var Backdrop = ({
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    Dialog.Backdrop,
    {
      className: clsx4(
        "fixed inset-0 z-40 bg-black/50",
        "data-[state=open]:animate-appear data-[state=closed]:animate-disappear",
        className
      ),
      ...props
    }
  );
};
Backdrop.displayName = "Modal.Backdrop";
var Positioner = ({
  children,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    Dialog.Positioner,
    {
      className: clsx4(
        "fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4",
        className
      ),
      ...props,
      children
    }
  );
};
Positioner.displayName = "Modal.Positioner";
var Content = ({
  children,
  className,
  size = "lg",
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    Dialog.Content,
    {
      className: clsx4(
        "relative flex w-full flex-col rounded-lg shadow-xl",
        "bg-white dark:bg-zinc-900",
        "border border-zinc-200 dark:border-zinc-800",
        "max-h-[90vh] overflow-y-auto",
        "data-[state=open]:animate-modal-in data-[state=closed]:animate-modal-out",
        "focus:outline-none",
        sizeClasses[size],
        className
      ),
      ...props,
      children
    }
  );
};
Content.displayName = "Modal.Content";
var Header = ({
  children,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: clsx4(
        "flex shrink-0 items-center justify-between p-6 border-b border-zinc-200 dark:border-zinc-800",
        className
      ),
      ...props,
      children
    }
  );
};
Header.displayName = "Modal.Header";
var Body = ({
  children,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx("div", { className: clsx4("min-h-0 overflow-y-auto p-6", className), ...props, children });
};
Body.displayName = "Modal.Body";
var Footer = ({
  children,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    "div",
    {
      className: clsx4(
        "flex shrink-0 items-center justify-end gap-3 p-6 border-t border-zinc-200 dark:border-zinc-800",
        className
      ),
      ...props,
      children
    }
  );
};
Footer.displayName = "Modal.Footer";
var Title = ({
  children,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    Dialog.Title,
    {
      className: clsx4(
        "text-lg font-semibold",
        "text-zinc-900 dark:text-zinc-100",
        className
      ),
      ...props,
      children
    }
  );
};
Title.displayName = "Modal.Title";
var Description = ({
  children,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    Dialog.Description,
    {
      className: clsx4("text-sm", "text-zinc-600 dark:text-zinc-400", className),
      ...props,
      children
    }
  );
};
Description.displayName = "Modal.Description";
var Modal = {
  Root: Root2,
  Trigger,
  Backdrop,
  Positioner,
  Content,
  Header,
  Body,
  Footer,
  Title,
  Description,
  Portal: Portal
};
var ConfirmationDialog = ({
  open,
  title,
  children,
  confirmLabel,
  cancelLabel = "Annuler",
  onConfirm,
  onCancel
}) => {
  return (
    // Mounted only while open: its portal nodes then come last in <body>,
    // above an open drawer of the same z-index that renders this popup.
    /* @__PURE__ */ jsx(Modal.Root, { open, lazyMount: true, unmountOnExit: true, role: "alertdialog", children: /* @__PURE__ */ jsxs(Modal.Portal, { children: [
      /* @__PURE__ */ jsx(Modal.Backdrop, {}),
      /* @__PURE__ */ jsx(Modal.Positioner, { children: /* @__PURE__ */ jsxs(Modal.Content, { size: "md", children: [
        /* @__PURE__ */ jsx(Modal.Header, { children: /* @__PURE__ */ jsx(Modal.Title, { children: title }) }),
        /* @__PURE__ */ jsx(Modal.Body, { children: /* @__PURE__ */ jsx(Modal.Description, { children }) }),
        /* @__PURE__ */ jsxs(Modal.Footer, { children: [
          /* @__PURE__ */ jsx(Button, { outline: true, onClick: onCancel, children: cancelLabel }),
          /* @__PURE__ */ jsx(Button, { onClick: onConfirm, children: confirmLabel })
        ] })
      ] }) })
    ] }) })
  );
};
ConfirmationDialog.displayName = "ConfirmationDialog";
function generateLoadingText(minLength, maxLength) {
  const length = Math.floor(Math.random() * (maxLength - minLength + 1)) + minLength;
  return "\u2588".repeat(length);
}
function generateIds(count) {
  return Array.from({ length: count }, () => newId("test"));
}
function LoadingText({
  minLength = 10,
  maxLength = 20,
  className = "text-gray-300"
}) {
  return /* @__PURE__ */ jsx("span", { className, children: generateLoadingText(minLength, maxLength) });
}
function LoadingBadge({
  className = "text-gray-300"
}) {
  return /* @__PURE__ */ jsx(
    "span",
    {
      className: cn(
        "inline-flex rounded-full bg-gray-100 px-2 font-semibold text-xs leading-5",
        className
      ),
      children: generateLoadingText(5, 12)
    }
  );
}
function LoadingMultiLine({
  className = "text-gray-300",
  line1Length = [8, 15],
  line2Length = [12, 20]
}) {
  return /* @__PURE__ */ jsxs("div", { className: "min-w-0", children: [
    /* @__PURE__ */ jsx("div", { className: cn("font-medium text-sm", className), children: generateLoadingText(line1Length[0], line1Length[1]) }),
    /* @__PURE__ */ jsx("div", { className: cn("text-sm", className), children: generateLoadingText(line2Length[0], line2Length[1]) })
  ] });
}
function LoadingIconText({
  className = "text-gray-300",
  textLength = [6, 12]
}) {
  return /* @__PURE__ */ jsxs("div", { className: "flex items-center", children: [
    /* @__PURE__ */ jsx("div", { className: "mr-1 h-4 w-4 rounded bg-gray-200" }),
    /* @__PURE__ */ jsx("span", { className: cn("font-medium text-sm", className), children: generateLoadingText(textLength[0], textLength[1]) })
  ] });
}
function generateLoadingCell(config, key) {
  const loadingType = config?.loadingCell;
  if (typeof loadingType === "function") {
    return /* @__PURE__ */ jsx("div", { children: loadingType() }, key);
  }
  switch (loadingType) {
    case "badge":
      return /* @__PURE__ */ jsx(LoadingBadge, {}, key);
    case "multiline":
      return /* @__PURE__ */ jsx(LoadingMultiLine, {}, key);
    case "icontext":
      return /* @__PURE__ */ jsx(LoadingIconText, {}, key);
    default:
      return /* @__PURE__ */ jsx(LoadingText, {}, key);
  }
}
function generateLoadingRows(columnCount, rowCount = 5, columns) {
  return Array.from(
    { length: rowCount },
    () => generateIds(columnCount).map((id, index) => {
      const columnConfig = columns?.[index];
      if (columnConfig) {
        return generateLoadingCell(columnConfig, id);
      }
      if (index === 0) {
        return /* @__PURE__ */ jsx("span", { className: "text-gray-300", children: generateLoadingText(8, 15) }, id);
      }
      if (index === 1) {
        return /* @__PURE__ */ jsx("span", { className: "text-gray-300", children: generateLoadingText(15, 25) }, id);
      }
      if (index === columnCount - 1) {
        return /* @__PURE__ */ jsx("span", { className: "text-gray-300", children: generateLoadingText(5, 10) }, id);
      }
      return /* @__PURE__ */ jsx("span", { className: "text-gray-300", children: generateLoadingText(10, 20) }, id);
    })
  );
}
function LoadingCell({ children }) {
  return /* @__PURE__ */ jsx("div", { className: "animate-pulse", children });
}
var TableContext = createContext(void 0);
var useTableContext = () => {
  const context = useContext(TableContext);
  return context || {};
};
function flattenChildren(children) {
  const childrenArray = Children.toArray(children);
  return childrenArray.reduce((flatChildren, child) => {
    if (child.type === Fragment$1) {
      return flatChildren.concat(
        flattenChildren(
          child.props.children
        )
      );
    }
    flatChildren.push(child);
    return flatChildren;
  }, []);
}
var TableRoot = forwardRef(
  ({
    className,
    children,
    striped = false,
    dense = false,
    grid = false,
    loading = false,
    stickyHeader = false,
    emptyState,
    isEmpty = false,
    ...props
  }, ref) => {
    const childrenArray = flattenChildren(children);
    const headerComponent = childrenArray.find(
      (child) => isValidElement(child) && child.type === TableHeader
    );
    const otherComponents = childrenArray.filter(
      (child) => !(isValidElement(child) && child.type === TableHeader)
    );
    return /* @__PURE__ */ jsx(
      TableContext.Provider,
      {
        value: {
          striped,
          dense,
          grid,
          loading,
          stickyHeader
        },
        children: /* @__PURE__ */ jsx(
          "div",
          {
            className: cn(
              "relative w-full overflow-auto rounded-lg border border-gray-200",
              stickyHeader && "max-h-[600px]",
              className
            ),
            ref,
            ...props,
            children: /* @__PURE__ */ jsxs(
              "table",
              {
                className: cn("w-full caption-bottom border-collapse text-sm"),
                "data-dense": dense || void 0,
                "data-grid": grid || void 0,
                "data-loading": loading || void 0,
                "data-sticky-header": stickyHeader || void 0,
                "data-striped": striped || void 0,
                children: [
                  headerComponent,
                  isEmpty && emptyState ? /* @__PURE__ */ jsx("tbody", { children: /* @__PURE__ */ jsx("tr", { children: /* @__PURE__ */ jsx("td", { className: "border-0 p-0", colSpan: 1e3, children: /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center py-12 text-gray-500", children: emptyState }) }) }) }) : otherComponents
                ]
              }
            )
          }
        )
      }
    );
  }
);
TableRoot.displayName = "Table";
var TableHeader = forwardRef(({ className, ...props }, ref) => {
  const { stickyHeader } = useTableContext();
  return /* @__PURE__ */ jsx(
    "thead",
    {
      className: cn(
        "border-gray-200 border-b bg-gray-50",
        stickyHeader && "sticky top-0 z-10 shadow-sm",
        className
      ),
      ref,
      ...props
    }
  );
});
TableHeader.displayName = "Table.Header";
var TableBody = forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    "tbody",
    {
      className: cn("[&_tr:last-child]:border-0", className),
      ref,
      ...props
    }
  );
});
TableBody.displayName = "Table.Body";
var TableFooter = forwardRef(({ className, ...props }, ref) => /* @__PURE__ */ jsx(
  "tfoot",
  {
    className: cn(
      "border-t bg-gray-50/50 font-medium [&>tr]:last:border-b-0",
      className
    ),
    ref,
    ...props
  }
));
TableFooter.displayName = "Table.Footer";
var TableRow = forwardRef(
  ({ className, clickable = false, selected = false, ...props }, ref) => {
    const { striped, grid } = useTableContext();
    return /* @__PURE__ */ jsx(
      "tr",
      {
        className: cn(
          "group/row border-gray-200 border-b transition-all duration-150 ease-out",
          striped && "even:bg-gray-50/50",
          "hover:bg-gray-50",
          clickable && "cursor-pointer",
          selected && "bg-blue-50 hover:bg-blue-100/70",
          grid && "divide-x divide-gray-200",
          className
        ),
        "data-selected": selected || void 0,
        ref,
        ...props
      }
    );
  }
);
TableRow.displayName = "Table.Row";
var TableHead = forwardRef(
  ({ className, children, sortable = false, sortDirection, onSort, ...props }, ref) => {
    const { dense, grid, stickyHeader } = useTableContext();
    return /* @__PURE__ */ jsx(
      "th",
      {
        className: cn(
          "bg-gray-50 text-left align-middle font-medium text-gray-700",
          dense ? "px-3 py-2 text-xs" : "px-4 py-3 text-sm",
          grid && "border-gray-200 border-x first:border-l-0 last:border-r-0",
          sortable && "cursor-pointer select-none transition-colors duration-150 hover:bg-gray-100",
          stickyHeader && "sticky top-0 z-20",
          // All headers sticky at top
          className
        ),
        onClick: sortable ? onSort : void 0,
        ref,
        ...props,
        children: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
          children,
          sortable && /* @__PURE__ */ jsx(TableSortIndicator, { direction: sortDirection, onClick: onSort })
        ] })
      }
    );
  }
);
TableHead.displayName = "Table.Head";
var TableCell = forwardRef(({ className, ...props }, ref) => {
  const { dense, grid } = useTableContext();
  return /* @__PURE__ */ jsx(
    "td",
    {
      className: cn(
        "align-middle transition-colors duration-150",
        dense ? "px-3 py-2 text-xs" : "px-4 py-3 text-sm",
        grid && "border-gray-200 border-x first:border-l-0 last:border-r-0",
        className
      ),
      ref,
      ...props
    }
  );
});
TableCell.displayName = "Table.Cell";
var TableSortIndicator = ({
  direction,
  onClick
}) => {
  const Icon = direction === "asc" ? CaretUpIcon : direction === "desc" ? CaretDownIcon : CaretUpDownIcon;
  return /* @__PURE__ */ jsx(
    "button",
    {
      className: "-mr-1 inline-flex text-gray-400 transition-colors hover:text-gray-600",
      onClick: (e) => {
        e.stopPropagation();
        onClick?.();
      },
      type: "button",
      children: /* @__PURE__ */ jsx(Icon, { size: 12, weight: "bold" })
    }
  );
};
var TableEmptyState = ({
  children,
  className
}) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn(
      "flex flex-col items-center justify-center py-12 text-center",
      className
    ),
    children: /* @__PURE__ */ jsx("div", { className: "text-gray-400 text-sm", children })
  }
);
var Table = Object.assign(TableRoot, {
  Header: TableHeader,
  Body: TableBody,
  Footer: TableFooter,
  Row: TableRow,
  Head: TableHead,
  Cell: TableCell,
  SortIndicator: TableSortIndicator,
  EmptyState: TableEmptyState
});
var useDataTableColumnVisibility = ({
  visibleColumns,
  onVisibleColumnsChange,
  columns
}) => {
  const [internalState, setInternalState] = useState(
    columns.reduce((acc, column) => {
      if (isNil(visibleColumns)) {
        return {};
      }
      return {
        // biome-ignore lint/performance/noAccumulatingSpread: Feels more readable
        ...acc,
        [column.id]: visibleColumns.includes(column.id)
      };
    }, {})
  );
  useEffect(() => {
    if (isNil(visibleColumns)) {
      setInternalState({});
      return;
    }
    setInternalState((currentState) => {
      return {
        ...Object.fromEntries(
          Object.keys(currentState).map((key) => [key, false])
        ),
        ...Object.fromEntries(visibleColumns.map((column) => [column, true]))
      };
    });
  }, [visibleColumns]);
  const handleVisibilityChange = useCallback(
    (updater) => {
      const nextState = functionalUpdate(updater, internalState);
      onVisibleColumnsChange?.(
        Object.keys(nextState).filter(
          (key) => nextState[key]
        )
      );
    },
    [internalState, onVisibleColumnsChange]
  );
  return {
    visibilityState: internalState,
    onColumnVisibilityChange: handleVisibilityChange
  };
};
var useDataTableColumns = ({
  columnSpecs,
  isTableSortable
}) => {
  const defs = useMemo(
    () => columnSpecs.map(({ cell: cellFn, ...column }) => ({
      id: column.id,
      header: column.header,
      accessorFn: typeof column.accessor === "function" ? column.accessor : (row) => row[column.accessor],
      enableSorting: (isTableSortable && column.isSortable) ?? false,
      ...isNil(cellFn) ? {} : {
        cell: ({ getValue, row }) => cellFn({
          value: getValue(),
          row: row.original,
          settings: row
        })
      },
      enableHiding: column.isHideable ?? true,
      meta: {
        ...column
      }
    })),
    [columnSpecs, isTableSortable]
  );
  return { columns: defs };
};
var useDataTableSelection = ({
  selectionState,
  onSelectionChange
}) => {
  const internalState = useMemo(() => {
    if (isNil(selectionState)) {
      return {};
    }
    return selectionState.reduce((acc, id) => {
      acc[id] = true;
      return acc;
    }, {});
  }, [selectionState]);
  const handleSelectionChange = useCallback(
    (updater) => {
      const nextState = functionalUpdate(updater, internalState);
      onSelectionChange?.(Object.keys(nextState));
    },
    [internalState, onSelectionChange]
  );
  return {
    selectionState: internalState,
    onSelectionChange: handleSelectionChange
  };
};
var toSortingState = (input) => [
  { id: input.id, desc: input.direction === "desc" }
];
var fromSortingState = (input) => isEmptyArray(input) ? null : {
  id: input[0].id,
  direction: input[0].desc ? "desc" : "asc"
};
var useDataTableSort = ({
  sortingState,
  onSortChange
}) => {
  const internalState = useMemo(() => {
    if (isNil(sortingState)) {
      return [];
    }
    return toSortingState(sortingState);
  }, [sortingState]);
  const handleSortingChange = useCallback(
    (updater) => {
      const nextState = functionalUpdate(updater, internalState);
      onSortChange?.(fromSortingState(nextState));
    },
    [internalState, onSortChange]
  );
  if (isNil(onSortChange)) {
    return {
      isTableSortable: false,
      sortingState: void 0,
      onSortingChange: void 0
    };
  }
  return {
    isTableSortable: true,
    sortingState: internalState,
    onSortingChange: handleSortingChange
  };
};
var DataTable = ({
  columns: columnSpecs,
  data,
  className,
  striped,
  grid,
  dense,
  stickyHeader,
  sorting,
  onSortChange,
  onRowClick,
  getRowLink,
  isLoading,
  onSelectionChange,
  selection,
  visibleColumns,
  onVisibleColumnsChange,
  emptyState,
  isEmpty
}) => {
  const { isTableSortable, sortingState, onSortingChange } = useDataTableSort({
    sortingState: sorting,
    onSortChange});
  const { selectionState, onSelectionChange: onRowSelectionChange } = useDataTableSelection({
    selectionState: selection,
    onSelectionChange
  });
  const { visibilityState, onColumnVisibilityChange } = useDataTableColumnVisibility({
    visibleColumns,
    onVisibleColumnsChange,
    columns: columnSpecs
  });
  const { columns } = useDataTableColumns({ columnSpecs, isTableSortable });
  const cachedDataRef = useRef([]);
  const isInitialRenderRef = useRef(true);
  useEffect(() => {
    if (!isLoading && data.length > 0) {
      cachedDataRef.current = data;
    }
    isInitialRenderRef.current = false;
  }, [data, isLoading]);
  const table = useReactTable({
    columns,
    data,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    enableSortingRemoval: true,
    sortDescFirst: true,
    state: {
      sorting: sortingState,
      rowSelection: selectionState,
      columnVisibility: visibilityState
    },
    onSortingChange,
    onRowSelectionChange,
    onColumnVisibilityChange,
    getRowId: (row) => row.id,
    initialState: {
      columnVisibility: visibilityState
    }
  });
  const visibleColumnCount = table.getVisibleFlatColumns().length;
  const loadingRowCount = isLoading ? !isInitialRenderRef.current && cachedDataRef.current.length > 0 ? cachedDataRef.current.length : 10 : 0;
  const loadingRows = isLoading ? generateLoadingRows(visibleColumnCount, loadingRowCount, columnSpecs) : [];
  return /* @__PURE__ */ jsxs(
    Table,
    {
      className,
      dense,
      grid,
      loading: isLoading,
      stickyHeader,
      striped,
      children: [
        /* @__PURE__ */ jsx(Table.Header, { children: table.getHeaderGroups().map((group) => /* @__PURE__ */ jsx(Table.Row, { children: group.headers.map((header) => /* @__PURE__ */ jsx(
          Table.Head,
          {
            className: cn(
              header.column.columnDef.meta?.headerClassName,
              header.column.getCanSort() && "cursor-pointer select-none hover:bg-gray-100"
            ),
            onClick: header.column.getCanSort() ? header.column.getToggleSortingHandler() : void 0,
            children: /* @__PURE__ */ jsxs("div", { className: "flex items-center gap-1", children: [
              flexRender(
                header.column.columnDef.header,
                header.getContext()
              ),
              header.column.getCanSort() && /* @__PURE__ */ jsx(
                Table.SortIndicator,
                {
                  direction: header.column.getIsSorted() || void 0
                }
              )
            ] })
          },
          header.id
        )) }, group.id)) }),
        /* @__PURE__ */ jsx(Table.Body, { children: isLoading ? loadingRows.map((row, rowIndex) => /* @__PURE__ */ jsx(Table.Row, { children: row.map((cell, cellIndex) => /* @__PURE__ */ jsx(Table.Cell, { children: cell }, `loading-${rowIndex}-${cellIndex}`)) }, `loading-${rowIndex}`)) : isEmpty || table.getRowModel().rows.length === 0 ? /* @__PURE__ */ jsx(Table.Row, { children: /* @__PURE__ */ jsx(Table.Cell, { className: "text-center", colSpan: visibleColumnCount, children: emptyState || /* @__PURE__ */ jsx(Table.EmptyState, { children: /* @__PURE__ */ jsx("p", { className: "text-gray-500", children: "No data available" }) }) }) }) : table.getRowModel().rows.map((row) => /* @__PURE__ */ jsx(
          Table.Row,
          {
            clickable: Boolean(onRowClick || getRowLink),
            onClick: onRowClick || getRowLink ? () => {
              if (getRowLink) {
                window.location.href = getRowLink(row.original);
              } else if (onRowClick) {
                onRowClick(row.original);
              }
            } : void 0,
            selected: row.getIsSelected(),
            children: row.getVisibleCells().map((cell) => /* @__PURE__ */ jsx(
              Table.Cell,
              {
                className: cell.column.columnDef.meta?.cellClassName,
                children: flexRender(cell.column.columnDef.cell, cell.getContext())
              },
              cell.id
            ))
          },
          row.id
        )) })
      ]
    }
  );
};
var DEFAULT_MIN = "1900-01-01";
var DEFAULT_MAX = "2099-12-31";
var TYPED_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
var toFrenchDate = (iso) => iso.split("-").reverse().join("/");
function parseTypedDate(text, min, max) {
  const match = TYPED_DATE.exec(text);
  if (!match) return void 0;
  const [, day, month, year] = match;
  const iso = `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
  try {
    parseDate(iso);
  } catch {
    return void 0;
  }
  return iso >= min && iso <= max ? iso : void 0;
}
var viewIcons = {
  prev: /* @__PURE__ */ jsx(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "16",
      height: "16",
      viewBox: "0 0 256 256",
      fill: "currentColor",
      "aria-hidden": "true",
      children: /* @__PURE__ */ jsx("path", { d: "M165.66,202.34a8,8,0,0,1-11.32,11.32l-80-80a8,8,0,0,1,0-11.32l80-80a8,8,0,0,1,11.32,11.32L91.31,128Z" })
    }
  ),
  next: /* @__PURE__ */ jsx(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "16",
      height: "16",
      viewBox: "0 0 256 256",
      fill: "currentColor",
      "aria-hidden": "true",
      children: /* @__PURE__ */ jsx("path", { d: "M181.66,133.66l-80,80a8,8,0,0,1-11.32-11.32L164.69,128,90.34,53.66a8,8,0,0,1,11.32-11.32l80,80A8,8,0,0,1,181.66,133.66Z" })
    }
  ),
  calendar: /* @__PURE__ */ jsx(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "18",
      height: "18",
      viewBox: "0 0 256 256",
      fill: "currentColor",
      "aria-hidden": "true",
      children: /* @__PURE__ */ jsx("path", { d: "M208,32H184V24a8,8,0,0,0-16,0v8H88V24a8,8,0,0,0-16,0v8H48A16,16,0,0,0,32,48V208a16,16,0,0,0,16,16H208a16,16,0,0,0,16-16V48A16,16,0,0,0,208,32ZM72,48v8a8,8,0,0,0,16,0V48h80v8a8,8,0,0,0,16,0V48h24V80H48V48ZM208,208H48V96H208V208Z" })
    }
  ),
  clear: /* @__PURE__ */ jsx(
    "svg",
    {
      xmlns: "http://www.w3.org/2000/svg",
      width: "16",
      height: "16",
      viewBox: "0 0 256 256",
      fill: "currentColor",
      "aria-hidden": "true",
      children: /* @__PURE__ */ jsx("path", { d: "M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z" })
    }
  )
};
var prevLabels = {
  day: "Mois pr\xE9c\xE9dent",
  month: "Ann\xE9e pr\xE9c\xE9dente",
  year: "D\xE9cennie pr\xE9c\xE9dente"
};
var nextLabels = {
  day: "Mois suivant",
  month: "Ann\xE9e suivante",
  year: "D\xE9cennie suivante"
};
var translations = {
  trigger: (open) => open ? "Fermer le calendrier" : "Ouvrir le calendrier",
  clearTrigger: "Effacer la date",
  prevTrigger: (view) => prevLabels[view],
  nextTrigger: (view) => nextLabels[view]
};
var cellTriggerClass = clsx4(
  "flex size-8 items-center justify-center rounded-md text-sm",
  "text-zinc-900 dark:text-zinc-100",
  "cursor-pointer select-none",
  "data-[today]:font-semibold",
  "data-[outside-range]:text-zinc-300 dark:data-[outside-range]:text-zinc-600",
  "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-40",
  "data-[selected]:bg-blue-600 data-[selected]:text-white",
  "not-data-[selected]:hover:bg-blue-50 dark:not-data-[selected]:hover:bg-blue-900/20"
);
function DatePicker({
  value,
  onValueChange,
  placeholder = "jj/mm/aaaa",
  disabled = false,
  label,
  id,
  min = DEFAULT_MIN,
  max = DEFAULT_MAX,
  className
}) {
  const inputRef = useRef(null);
  const commitTypedText = (input) => {
    const text = input.value.trim();
    const iso = text ? parseTypedDate(text, min, max) : void 0;
    const valid = !text || Boolean(iso);
    input.setCustomValidity(
      valid ? "" : `Saisissez une date au format jj/mm/aaaa, entre le ${toFrenchDate(min)} et le ${toFrenchDate(max)}.`
    );
    if (iso && iso !== value) onValueChange?.(iso);
    return valid;
  };
  return /* @__PURE__ */ jsxs(
    DatePicker$1.Root,
    {
      id,
      min: parseDate(min),
      max: parseDate(max),
      value: value ? [parseDate(value)] : [],
      onValueChange: (details) => {
        inputRef.current?.setCustomValidity("");
        onValueChange?.(details.value[0]?.toString() ?? "");
      },
      disabled,
      locale: "fr-FR",
      translations,
      className: clsx4("relative w-full", className),
      children: [
        label && /* @__PURE__ */ jsx(DatePicker$1.Label, { className: "mb-1 block text-sm font-medium text-zinc-700 dark:text-zinc-300", children: label }),
        /* @__PURE__ */ jsxs(DatePicker$1.Control, { className: "relative flex items-center", children: [
          /* @__PURE__ */ jsx(
            DatePicker$1.Input,
            {
              ref: inputRef,
              placeholder,
              fixOnBlur: false,
              onBlur: (event) => commitTypedText(event.currentTarget),
              onKeyDownCapture: (event) => {
                if (event.key !== "Enter") return;
                event.preventDefault();
                if (!commitTypedText(event.currentTarget)) {
                  event.currentTarget.reportValidity();
                }
              },
              onInput: (event) => event.currentTarget.setCustomValidity(""),
              className: clsx4(
                "w-full rounded-lg border border-zinc-300 dark:border-zinc-700",
                "invalid:border-red-500",
                "bg-white dark:bg-zinc-900",
                "px-3 py-2 pr-16",
                "text-sm text-zinc-900 dark:text-zinc-100",
                "placeholder:text-zinc-500 dark:placeholder:text-zinc-400",
                "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent",
                "disabled:bg-zinc-100 dark:disabled:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50"
              )
            }
          ),
          /* @__PURE__ */ jsx(
            DatePicker$1.ClearTrigger,
            {
              className: clsx4(
                "absolute right-9 top-1/2 -translate-y-1/2",
                "text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200"
              ),
              children: viewIcons.clear
            }
          ),
          /* @__PURE__ */ jsx(
            DatePicker$1.Trigger,
            {
              className: clsx4(
                "absolute right-2 top-1/2 -translate-y-1/2",
                "text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200",
                "disabled:opacity-50"
              ),
              children: viewIcons.calendar
            }
          )
        ] }),
        /* @__PURE__ */ jsx(Portal, { children: /* @__PURE__ */ jsx(DatePicker$1.Positioner, { children: /* @__PURE__ */ jsxs(
          DatePicker$1.Content,
          {
            className: clsx4(
              "mt-1 rounded-lg border border-zinc-300 dark:border-zinc-700",
              "bg-white dark:bg-zinc-900",
              "shadow-lg p-2",
              "z-[60]",
              "data-[state=open]:animate-appear data-[state=closed]:animate-disappear"
            ),
            children: [
              /* @__PURE__ */ jsx(DatePicker$1.View, { view: "day", children: /* @__PURE__ */ jsx(DatePicker$1.Context, { children: (api) => /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsxs(DatePicker$1.ViewControl, { className: "mb-2 flex items-center justify-between", children: [
                  /* @__PURE__ */ jsx(DatePicker$1.PrevTrigger, { className: "rounded p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800", children: viewIcons.prev }),
                  /* @__PURE__ */ jsx(DatePicker$1.ViewTrigger, { className: "rounded px-2 py-1 text-sm font-medium capitalize text-zinc-900 hover:bg-zinc-100 dark:text-zinc-100 dark:hover:bg-zinc-800", children: /* @__PURE__ */ jsx(DatePicker$1.RangeText, {}) }),
                  /* @__PURE__ */ jsx(DatePicker$1.NextTrigger, { className: "rounded p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800", children: viewIcons.next })
                ] }),
                /* @__PURE__ */ jsxs(DatePicker$1.Table, { children: [
                  /* @__PURE__ */ jsx(DatePicker$1.TableHead, { children: /* @__PURE__ */ jsx(DatePicker$1.TableRow, { children: api.weekDays.map((weekDay) => /* @__PURE__ */ jsx(
                    DatePicker$1.TableHeader,
                    {
                      className: "size-8 text-xs font-medium text-zinc-500 dark:text-zinc-400",
                      children: weekDay.narrow
                    },
                    weekDay.long
                  )) }) }),
                  /* @__PURE__ */ jsx(DatePicker$1.TableBody, { children: api.weeks.map((week, weekIndex) => /* @__PURE__ */ jsx(DatePicker$1.TableRow, { children: week.map((day) => /* @__PURE__ */ jsx(
                    DatePicker$1.TableCell,
                    {
                      value: day,
                      children: /* @__PURE__ */ jsx(
                        DatePicker$1.TableCellTrigger,
                        {
                          className: cellTriggerClass,
                          children: day.day
                        }
                      )
                    },
                    day.toString()
                  )) }, `week-${weekIndex}`)) })
                ] })
              ] }) }) }),
              /* @__PURE__ */ jsx(DatePicker$1.View, { view: "month", children: /* @__PURE__ */ jsx(DatePicker$1.Context, { children: (api) => /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsxs(DatePicker$1.ViewControl, { className: "mb-2 flex items-center justify-between", children: [
                  /* @__PURE__ */ jsx(DatePicker$1.PrevTrigger, { className: "rounded p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800", children: viewIcons.prev }),
                  /* @__PURE__ */ jsx(DatePicker$1.ViewTrigger, { className: "rounded px-2 py-1 text-sm font-medium text-zinc-900 hover:bg-zinc-100 dark:text-zinc-100 dark:hover:bg-zinc-800", children: /* @__PURE__ */ jsx(DatePicker$1.RangeText, {}) }),
                  /* @__PURE__ */ jsx(DatePicker$1.NextTrigger, { className: "rounded p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800", children: viewIcons.next })
                ] }),
                /* @__PURE__ */ jsx(DatePicker$1.Table, { children: /* @__PURE__ */ jsx(DatePicker$1.TableBody, { children: api.getMonthsGrid({ columns: 4, format: "short" }).map((months, rowIndex) => /* @__PURE__ */ jsx(
                  DatePicker$1.TableRow,
                  {
                    children: months.map((month) => /* @__PURE__ */ jsx(
                      DatePicker$1.TableCell,
                      {
                        value: month.value,
                        children: /* @__PURE__ */ jsx(
                          DatePicker$1.TableCellTrigger,
                          {
                            className: clsx4(
                              cellTriggerClass,
                              "w-full capitalize"
                            ),
                            children: month.label
                          }
                        )
                      },
                      month.value
                    ))
                  },
                  `month-row-${rowIndex}`
                )) }) })
              ] }) }) }),
              /* @__PURE__ */ jsx(DatePicker$1.View, { view: "year", children: /* @__PURE__ */ jsx(DatePicker$1.Context, { children: (api) => /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsxs(DatePicker$1.ViewControl, { className: "mb-2 flex items-center justify-between", children: [
                  /* @__PURE__ */ jsx(DatePicker$1.PrevTrigger, { className: "rounded p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800", children: viewIcons.prev }),
                  /* @__PURE__ */ jsx(DatePicker$1.ViewTrigger, { className: "rounded px-2 py-1 text-sm font-medium text-zinc-900 hover:bg-zinc-100 dark:text-zinc-100 dark:hover:bg-zinc-800", children: /* @__PURE__ */ jsx(DatePicker$1.RangeText, {}) }),
                  /* @__PURE__ */ jsx(DatePicker$1.NextTrigger, { className: "rounded p-1 hover:bg-zinc-100 dark:hover:bg-zinc-800", children: viewIcons.next })
                ] }),
                /* @__PURE__ */ jsx(DatePicker$1.Table, { children: /* @__PURE__ */ jsx(DatePicker$1.TableBody, { children: api.getYearsGrid({ columns: 4 }).map((years, rowIndex) => /* @__PURE__ */ jsx(
                  DatePicker$1.TableRow,
                  {
                    children: years.map((year) => /* @__PURE__ */ jsx(
                      DatePicker$1.TableCell,
                      {
                        value: year.value,
                        children: /* @__PURE__ */ jsx(
                          DatePicker$1.TableCellTrigger,
                          {
                            className: clsx4(cellTriggerClass, "w-full"),
                            children: year.label
                          }
                        )
                      },
                      year.value
                    ))
                  },
                  `year-row-${rowIndex}`
                )) }) })
              ] }) }) })
            ]
          }
        ) }) })
      ]
    }
  );
}
DatePicker.displayName = "DatePicker";
var Root3 = ({ children, ...props }) => {
  return /* @__PURE__ */ jsx(Dialog.Root, { ...props, children });
};
Root3.displayName = "Drawer.Root";
var Trigger2 = ({
  children,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    Dialog.Trigger,
    {
      className: clsx4(
        "inline-flex items-center gap-x-2 rounded-lg border px-3.5 py-2.5 text-base/6 font-semibold sm:px-3 sm:py-1.5 sm:text-sm/6",
        "border-zinc-950/10 text-zinc-950 data-active:bg-zinc-950/2.5 data-hover:bg-zinc-950/2.5",
        "dark:border-white/15 dark:text-white dark:data-active:bg-white/5 dark:data-hover:bg-white/5",
        "focus:outline-none data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-blue-500",
        "data-disabled:opacity-50 data-disabled:cursor-not-allowed",
        className
      ),
      ...props,
      children
    }
  );
};
Trigger2.displayName = "Drawer.Trigger";
var Backdrop2 = ({
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    Dialog.Backdrop,
    {
      className: clsx4(
        "fixed inset-0 z-40 bg-black/50",
        "data-[state=open]:animate-appear data-[state=closed]:animate-disappear",
        className
      ),
      ...props
    }
  );
};
Backdrop2.displayName = "Drawer.Backdrop";
var Positioner2 = ({
  children,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    Dialog.Positioner,
    {
      className: clsx4("fixed inset-0 z-50 overflow-y-auto", className),
      ...props,
      children
    }
  );
};
Positioner2.displayName = "Drawer.Positioner";
var Content2 = ({
  children,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    Dialog.Content,
    {
      className: clsx4(
        "fixed right-0 top-0 h-full max-h-screen w-[var(--width-side-tray-small)] shadow-xl",
        "bg-white dark:bg-zinc-900",
        "border-l border-zinc-200 dark:border-zinc-800",
        "flex flex-col overflow-hidden",
        "data-[state=open]:animate-slide-in data-[state=closed]:animate-slide-out",
        "focus:outline-none",
        className
      ),
      ...props,
      children
    }
  );
};
Content2.displayName = "Drawer.Content";
var Title2 = ({
  children,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    Dialog.Title,
    {
      className: clsx4(
        "text-lg font-semibold",
        "text-zinc-900 dark:text-zinc-100",
        className
      ),
      ...props,
      children
    }
  );
};
Title2.displayName = "Drawer.Title";
var Description2 = ({
  children,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    Dialog.Description,
    {
      className: clsx4("text-sm", "text-zinc-600 dark:text-zinc-400", className),
      ...props,
      children
    }
  );
};
Description2.displayName = "Drawer.Description";
var CloseTrigger = ({
  children,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    Dialog.CloseTrigger,
    {
      className: clsx4(
        "inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md",
        "text-zinc-600 dark:text-zinc-400",
        "data-hover:bg-zinc-100 dark:data-hover:bg-zinc-800",
        "focus:outline-none data-focus:outline-2 data-focus:outline-offset-2 data-focus:outline-blue-500",
        "transition-colors",
        className
      ),
      ...props,
      children: children ?? "\xD7"
    }
  );
};
CloseTrigger.displayName = "Drawer.CloseTrigger";
var Drawer = {
  Root: Root3,
  Trigger: Trigger2,
  Backdrop: Backdrop2,
  Positioner: Positioner2,
  Content: Content2,
  Title: Title2,
  Description: Description2,
  CloseTrigger,
  Portal: Portal
};
var GroupedTableContext = createContext(
  void 0
);
var useGroupedTableContext = () => {
  const context = useContext(GroupedTableContext);
  return context || {};
};
var GroupedTableRoot = forwardRef(
  ({
    className,
    children,
    striped = false,
    dense = false,
    grid = false,
    loading = false,
    stickyHeader = false,
    emptyState,
    isEmpty = false,
    ...props
  }, ref) => {
    return /* @__PURE__ */ jsx(
      GroupedTableContext.Provider,
      {
        value: {
          striped,
          dense,
          grid,
          loading,
          stickyHeader
        },
        children: /* @__PURE__ */ jsx(
          "div",
          {
            className: cn(
              "relative w-full overflow-auto rounded-lg border border-gray-200",
              stickyHeader && "max-h-[600px]",
              className
            ),
            ref,
            ...props,
            children: /* @__PURE__ */ jsx(
              "table",
              {
                className: cn("w-full caption-bottom border-collapse text-sm"),
                "data-dense": dense || void 0,
                "data-grid": grid || void 0,
                "data-loading": loading || void 0,
                "data-sticky-header": stickyHeader || void 0,
                "data-striped": striped || void 0,
                children: isEmpty && emptyState ? /* @__PURE__ */ jsx("tbody", { children: /* @__PURE__ */ jsx("tr", { children: /* @__PURE__ */ jsx("td", { className: "border-0 p-0", colSpan: 1e3, children: /* @__PURE__ */ jsx("div", { className: "flex items-center justify-center py-12 text-gray-500", children: emptyState }) }) }) }) : children
              }
            )
          }
        )
      }
    );
  }
);
GroupedTableRoot.displayName = "GroupedTable";
var GroupedTableHeader = forwardRef(({ className, ...props }, ref) => {
  const { stickyHeader } = useGroupedTableContext();
  return /* @__PURE__ */ jsx(
    "thead",
    {
      className: cn(
        "border-gray-200 border-b bg-gray-50",
        stickyHeader && "sticky top-0 z-10 shadow-sm",
        className
      ),
      ref,
      ...props
    }
  );
});
GroupedTableHeader.displayName = "GroupedTable.Header";
var GroupedTableBody = forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    "tbody",
    {
      className: cn("[&_tr:last-child]:border-0", className),
      ref,
      ...props
    }
  );
});
GroupedTableBody.displayName = "GroupedTable.Body";
var GroupedTableRow = forwardRef(
  ({ className, clickable = false, selected = false, ...props }, ref) => {
    const { striped, grid } = useGroupedTableContext();
    return /* @__PURE__ */ jsx(
      "tr",
      {
        className: cn(
          "group/row border-gray-200 border-b transition-all duration-150 ease-out",
          striped && "even:bg-gray-50/50",
          "hover:bg-gray-50",
          clickable && "cursor-pointer",
          selected && "bg-blue-50 hover:bg-blue-100/70",
          grid && "divide-x divide-gray-200",
          className
        ),
        "data-selected": selected || void 0,
        ref,
        ...props
      }
    );
  }
);
GroupedTableRow.displayName = "GroupedTable.Row";
var GroupedTableHead = forwardRef(({ className, children, ...props }, ref) => {
  const { dense, grid, stickyHeader } = useGroupedTableContext();
  return /* @__PURE__ */ jsx(
    "th",
    {
      className: cn(
        "bg-gray-50 text-left align-middle font-medium text-gray-700",
        dense ? "px-3 py-2 text-xs" : "px-4 py-3 text-sm",
        grid && "border-gray-200 border-x first:border-l-0 last:border-r-0",
        stickyHeader && "sticky top-0 z-20",
        className
      ),
      ref,
      ...props,
      children
    }
  );
});
GroupedTableHead.displayName = "GroupedTable.Head";
var GroupedTableHeaderRow = forwardRef(({ className, ...props }, ref) => {
  const { grid } = useGroupedTableContext();
  return /* @__PURE__ */ jsx(
    "tr",
    {
      className: cn(
        "border-gray-200 border-b",
        grid && "divide-x divide-gray-200",
        className
      ),
      ref,
      ...props
    }
  );
});
GroupedTableHeaderRow.displayName = "GroupedTable.HeaderRow";
var GroupedTableHeaderGroup = forwardRef(({ className, children, colSpan, rowSpan, ...props }, ref) => {
  const { dense, grid, stickyHeader } = useGroupedTableContext();
  return /* @__PURE__ */ jsx(
    "th",
    {
      className: cn(
        "bg-gray-100 text-center align-middle font-semibold text-gray-800",
        dense ? "px-3 py-1.5 text-xs" : "px-4 py-2 text-sm",
        grid && "border-gray-200 border-x first:border-l-0 last:border-r-0",
        stickyHeader && "sticky top-0 z-20",
        className
      ),
      colSpan,
      rowSpan,
      ref,
      ...props,
      children
    }
  );
});
GroupedTableHeaderGroup.displayName = "GroupedTable.HeaderGroup";
var GroupedTableCell = forwardRef(({ className, ...props }, ref) => {
  const { dense, grid } = useGroupedTableContext();
  return /* @__PURE__ */ jsx(
    "td",
    {
      className: cn(
        "align-middle transition-colors duration-150",
        dense ? "px-3 py-2 text-xs" : "px-4 py-3 text-sm",
        grid && "border-gray-200 border-x first:border-l-0 last:border-r-0",
        className
      ),
      ref,
      ...props
    }
  );
});
GroupedTableCell.displayName = "GroupedTable.Cell";
var GroupedTableGroup = ({
  groupKey,
  children,
  defaultExpanded = true
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  return /* @__PURE__ */ jsx(Fragment$1, { children: typeof children === "function" ? children({
    isExpanded,
    toggleExpanded: () => setIsExpanded(!isExpanded)
  }) : children }, groupKey);
};
GroupedTableGroup.displayName = "GroupedTable.Group";
var GroupedTableGroupRow = forwardRef(
  ({
    className,
    children,
    rowSpan,
    isExpanded,
    onToggle,
    showExpandIcon = true,
    ...props
  }, ref) => {
    const { grid } = useGroupedTableContext();
    return /* @__PURE__ */ jsxs(
      "tr",
      {
        className: cn(
          "group/row border-gray-200 border-b bg-gray-50/30 transition-all duration-150 ease-out",
          "hover:bg-gray-100/50",
          grid && "divide-x divide-gray-200",
          className
        ),
        ref,
        ...props,
        children: [
          showExpandIcon && onToggle && /* @__PURE__ */ jsx(
            "td",
            {
              className: cn(
                "w-8 select-none align-middle",
                grid && "border-gray-200 border-x first:border-l-0"
              ),
              rowSpan: isExpanded ? 1 : rowSpan,
              children: /* @__PURE__ */ jsx(
                "button",
                {
                  className: "flex w-full cursor-pointer items-center justify-center p-1 text-gray-500 transition-colors hover:text-gray-700",
                  type: "button",
                  onClick: onToggle,
                  "aria-expanded": isExpanded,
                  "aria-label": isExpanded ? "Collapse group" : "Expand group",
                  children: isExpanded ? /* @__PURE__ */ jsx(CaretDown, { size: 16, weight: "bold" }) : /* @__PURE__ */ jsx(CaretRight, { size: 16, weight: "bold" })
                }
              )
            }
          ),
          children
        ]
      }
    );
  }
);
GroupedTableGroupRow.displayName = "GroupedTable.GroupRow";
var GroupedTableEmptyState = ({
  children,
  className
}) => /* @__PURE__ */ jsx(
  "div",
  {
    className: cn(
      "flex flex-col items-center justify-center py-12 text-center",
      className
    ),
    children: /* @__PURE__ */ jsx("div", { className: "text-gray-400 text-sm", children })
  }
);
var GroupedTable = Object.assign(GroupedTableRoot, {
  Header: GroupedTableHeader,
  HeaderRow: GroupedTableHeaderRow,
  HeaderGroup: GroupedTableHeaderGroup,
  Body: GroupedTableBody,
  Row: GroupedTableRow,
  Head: GroupedTableHead,
  Cell: GroupedTableCell,
  Group: GroupedTableGroup,
  GroupRow: GroupedTableGroupRow,
  EmptyState: GroupedTableEmptyState
});
var sizes = {
  sm: "w-3 h-3",
  md: "w-4 h-4",
  lg: "w-4 h-4",
  xl: "w-5 h-5"
};
var LoadingSpinner = ({
  size = "md",
  className
}) => {
  return /* @__PURE__ */ jsxs(
    "svg",
    {
      "aria-hidden": "true",
      className: cn("animate-spin", sizes[size], className),
      fill: "none",
      viewBox: "0 0 24 24",
      xmlns: "http://www.w3.org/2000/svg",
      children: [
        /* @__PURE__ */ jsx(
          "circle",
          {
            className: "opacity-25",
            cx: "12",
            cy: "12",
            r: "10",
            stroke: "currentColor",
            strokeWidth: "4"
          }
        ),
        /* @__PURE__ */ jsx(
          "path",
          {
            className: "opacity-75",
            d: "M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z",
            fill: "currentColor"
          }
        )
      ]
    }
  );
};
var MenuContext = createContext({ withPortal: true });
var MenuRoot = ({ children, withPortal = true, ...props }) => {
  return /* @__PURE__ */ jsx(MenuContext.Provider, { value: { withPortal, disabled: props.disabled }, children: /* @__PURE__ */ jsx(Menu$1.Root, { ...props, children }) });
};
MenuRoot.displayName = "Menu";
var Trigger3 = ({
  children,
  className,
  plain,
  outline,
  ...props
}) => {
  const { disabled } = useContext(MenuContext);
  const buttonProps = plain ? { plain: true } : outline ? { outline: true } : { outline: true };
  return /* @__PURE__ */ jsx(Menu$1.Trigger, { asChild: true, ...props, children: /* @__PURE__ */ jsx(Button, { ...buttonProps, className, disabled, children }) });
};
Trigger3.displayName = "Menu.Trigger";
var Content3 = ({
  children,
  className,
  ...props
}) => {
  const { withPortal } = useContext(MenuContext);
  const content = /* @__PURE__ */ jsx(Menu$1.Positioner, { children: /* @__PURE__ */ jsx(
    Menu$1.Content,
    {
      className: clsx4(
        "z-50 min-w-[12rem] rounded-lg border shadow-lg",
        "border-zinc-200 dark:border-zinc-800",
        "bg-white dark:bg-zinc-900",
        "p-1",
        "data-[state=open]:animate-appear data-[state=closed]:animate-disappear",
        "origin-[var(--transform-origin)]",
        className
      ),
      ...props,
      children
    }
  ) });
  return withPortal ? /* @__PURE__ */ jsx(Portal, { children: content }) : content;
};
Content3.displayName = "Menu.Content";
var Item = ({
  children,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    Menu$1.Item,
    {
      className: clsx4(
        "relative flex cursor-pointer select-none items-center gap-x-2 rounded-md px-2 py-1.5 text-sm outline-none",
        "text-zinc-900 dark:text-zinc-100",
        "data-highlighted:bg-zinc-100 dark:data-highlighted:bg-zinc-800",
        "data-disabled:opacity-50 data-disabled:cursor-not-allowed",
        "transition-colors",
        className
      ),
      ...props,
      children
    }
  );
};
Item.displayName = "Menu.Item";
var Menu = Object.assign(MenuRoot, {
  Trigger: Trigger3,
  Content: Content3,
  Item
});
var Pagination = forwardRef(
  ({ className, ...props }, ref) => {
    return /* @__PURE__ */ jsxs(
      Pagination$1.Root,
      {
        ref,
        className: clsx4("flex items-center gap-2", className),
        ...props,
        children: [
          /* @__PURE__ */ jsx(PaginationPrevTrigger, {}),
          /* @__PURE__ */ jsx(Pagination$1.Context, { children: (api) => api.pages.map(
            (page, index) => page.type === "page" ? /* @__PURE__ */ jsx(PaginationItem, { type: "page", value: page.value, children: page.value }, index) : /* @__PURE__ */ jsx(PaginationEllipsis, { index }, index)
          ) }),
          /* @__PURE__ */ jsx(PaginationNextTrigger, {})
        ]
      }
    );
  }
);
Pagination.displayName = "Pagination";
var PaginationItem = forwardRef(({ className, children, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    Pagination$1.Item,
    {
      ref,
      className: clsx4(
        "flex size-9 items-center justify-center rounded-lg border border-zinc-950/10 bg-white text-sm font-medium text-zinc-950 transition-colors hover:bg-zinc-50 data-selected:border-blue-600 data-selected:bg-blue-600 data-selected:text-white data-disabled:cursor-not-allowed data-disabled:opacity-50 dark:border-white/15 dark:bg-zinc-900 dark:text-white dark:hover:bg-zinc-800 dark:data-selected:border-blue-500 dark:data-selected:bg-blue-500",
        className
      ),
      ...props,
      children
    }
  );
});
PaginationItem.displayName = "PaginationItem";
var PaginationPrevTrigger = forwardRef(({ className, children, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    Pagination$1.PrevTrigger,
    {
      ref,
      className: clsx4(
        "flex size-9 items-center justify-center rounded-lg border border-zinc-950/10 bg-white text-zinc-950 transition-colors hover:bg-zinc-50 data-disabled:cursor-not-allowed data-disabled:opacity-50 dark:border-white/15 dark:bg-zinc-900 dark:text-white dark:hover:bg-zinc-800",
        className
      ),
      ...props,
      children: children || /* @__PURE__ */ jsx(CaretLeftIcon, { className: "size-4" })
    }
  );
});
PaginationPrevTrigger.displayName = "PaginationPrevTrigger";
var PaginationNextTrigger = forwardRef(({ className, children, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    Pagination$1.NextTrigger,
    {
      ref,
      className: clsx4(
        "flex size-9 items-center justify-center rounded-lg border border-zinc-950/10 bg-white text-zinc-950 transition-colors hover:bg-zinc-50 data-disabled:cursor-not-allowed data-disabled:opacity-50 dark:border-white/15 dark:bg-zinc-900 dark:text-white dark:hover:bg-zinc-800",
        className
      ),
      ...props,
      children: children || /* @__PURE__ */ jsx(CaretRightIcon, { className: "size-4" })
    }
  );
});
PaginationNextTrigger.displayName = "PaginationNextTrigger";
var PaginationEllipsis = forwardRef(({ className, ...props }, ref) => {
  return /* @__PURE__ */ jsx(
    Pagination$1.Ellipsis,
    {
      ref,
      className: clsx4(
        "flex size-9 items-center justify-center text-sm text-zinc-500 dark:text-zinc-400",
        className
      ),
      ...props,
      children: "..."
    }
  );
});
PaginationEllipsis.displayName = "PaginationEllipsis";
function matchesPrefix(item, normalizedQuery) {
  const label = item.label.toLowerCase();
  const value = String(item.value).toLowerCase();
  return label.startsWith(normalizedQuery) || value.startsWith(normalizedQuery);
}
function Select({
  options,
  value,
  defaultValue,
  onValueChange,
  placeholder = "Select...",
  disabled = false,
  required = false,
  multiple = false,
  label,
  groupBy,
  flat = false,
  searchable = false,
  title,
  className
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const collection = createListCollection({
    items: options,
    itemToString: (item) => item.label,
    itemToValue: (item) => item.value,
    ...groupBy && { groupBy }
  });
  const hoverTitle = title ?? (flat ? options.filter((item) => value?.includes(item.value)).map((item) => item.label).join(", ") || void 0 : void 0);
  const normalizedQuery = searchable ? searchQuery.trim().toLowerCase() : "";
  const filteredOptions = normalizedQuery ? options.filter((item) => matchesPrefix(item, normalizedQuery)) : options;
  const visibleCollection = normalizedQuery ? createListCollection({
    items: filteredOptions,
    itemToString: (item) => item.label,
    itemToValue: (item) => item.value,
    ...groupBy && { groupBy }
  }) : collection;
  const handleValueChange = (details) => {
    if (onValueChange) {
      onValueChange({ value: details.value });
    }
  };
  const handleOpenChange = (details) => {
    if (!details.open) {
      setSearchQuery("");
    }
  };
  const handleSearchKeyDown = (event) => {
    if (event.key !== "Escape") {
      event.stopPropagation();
    }
  };
  const renderItem = (item) => /* @__PURE__ */ jsxs(
    Select$1.Item,
    {
      item,
      className: clsx4(
        "relative flex items-center justify-between",
        "px-3 py-2 text-sm",
        "cursor-pointer select-none",
        "text-zinc-900 dark:text-zinc-100",
        "data-[highlighted]:bg-blue-50 dark:data-[highlighted]:bg-blue-900/20",
        "data-[state=checked]:bg-blue-100 dark:data-[state=checked]:bg-blue-900/30",
        "data-[disabled]:opacity-50 data-[disabled]:cursor-not-allowed"
      ),
      children: [
        /* @__PURE__ */ jsx(Select$1.ItemText, { children: item.label }),
        /* @__PURE__ */ jsx(Select$1.ItemIndicator, { className: "text-blue-600 dark:text-blue-400", children: "\u2713" })
      ]
    },
    item.value
  );
  return /* @__PURE__ */ jsxs(
    Select$1.Root,
    {
      collection,
      onValueChange: handleValueChange,
      onOpenChange: handleOpenChange,
      disabled,
      required,
      multiple,
      defaultValue,
      value,
      className: clsx4("relative w-full min-w-0", className),
      children: [
        label && /* @__PURE__ */ jsx(Select$1.Label, { className: "block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1", children: label }),
        /* @__PURE__ */ jsxs(Select$1.Control, { className: "relative", children: [
          /* @__PURE__ */ jsxs(
            Select$1.Trigger,
            {
              title: hoverTitle,
              className: clsx4(
                "w-full flex items-center justify-between",
                "text-sm text-zinc-900 dark:text-zinc-100",
                "focus:outline-none focus:ring-2",
                flat ? [
                  "rounded-md border border-transparent bg-transparent px-2 py-1",
                  "hover:border-zinc-200 dark:hover:border-zinc-700",
                  "focus:ring-blue-500/20 focus:border-blue-500",
                  "disabled:cursor-not-allowed disabled:opacity-50"
                ] : [
                  "rounded-lg border border-zinc-300 dark:border-zinc-700",
                  "bg-white dark:bg-zinc-900",
                  "px-3 py-2",
                  "focus:ring-blue-500 focus:border-transparent",
                  "disabled:bg-zinc-100 dark:disabled:bg-zinc-800 disabled:cursor-not-allowed disabled:opacity-50"
                ]
              ),
              children: [
                /* @__PURE__ */ jsx(
                  Select$1.ValueText,
                  {
                    placeholder,
                    className: clsx4(
                      "text-zinc-900 dark:text-zinc-100 data-[placeholder-shown]:text-zinc-500 dark:data-[placeholder-shown]:text-zinc-400",
                      flat && "min-w-0 truncate"
                    )
                  }
                ),
                /* @__PURE__ */ jsx(Select$1.Indicator, { className: "shrink-0 text-zinc-500 dark:text-zinc-400 ml-2", children: /* @__PURE__ */ jsxs(
                  "svg",
                  {
                    xmlns: "http://www.w3.org/2000/svg",
                    width: "20",
                    height: "20",
                    viewBox: "0 0 256 256",
                    fill: "currentColor",
                    role: "img",
                    "aria-label": "Toggle dropdown",
                    children: [
                      /* @__PURE__ */ jsx("title", { children: "Toggle dropdown" }),
                      /* @__PURE__ */ jsx("path", { d: "M213.66,101.66l-80,80a8,8,0,0,1-11.32,0l-80-80A8,8,0,0,1,53.66,90.34L128,164.69l74.34-74.35a8,8,0,0,1,11.32,11.32Z" })
                    ]
                  }
                ) })
              ]
            }
          ),
          /* @__PURE__ */ jsx(
            Select$1.ClearTrigger,
            {
              className: clsx4(
                "absolute right-9 top-1/2 -translate-y-1/2",
                "text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200",
                "hidden data-[value]:block"
              ),
              children: /* @__PURE__ */ jsxs(
                "svg",
                {
                  xmlns: "http://www.w3.org/2000/svg",
                  width: "16",
                  height: "16",
                  viewBox: "0 0 256 256",
                  fill: "currentColor",
                  role: "img",
                  "aria-label": "Clear selection",
                  children: [
                    /* @__PURE__ */ jsx("title", { children: "Clear selection" }),
                    /* @__PURE__ */ jsx("path", { d: "M205.66,194.34a8,8,0,0,1-11.32,11.32L128,139.31,61.66,205.66a8,8,0,0,1-11.32-11.32L116.69,128,50.34,61.66A8,8,0,0,1,61.66,50.34L128,116.69l66.34-66.35a8,8,0,0,1,11.32,11.32L139.31,128Z" })
                  ]
                }
              )
            }
          )
        ] }),
        /* @__PURE__ */ jsx(Portal, { children: /* @__PURE__ */ jsx(Select$1.Positioner, { className: "!w-[var(--reference-width)]", children: /* @__PURE__ */ jsxs(
          Select$1.Content,
          {
            className: clsx4(
              "mt-1 w-full rounded-lg border border-zinc-300 dark:border-zinc-700",
              "bg-white dark:bg-zinc-900",
              "shadow-lg",
              "max-h-60 overflow-auto",
              "z-[60]",
              "data-[state=open]:animate-appear data-[state=closed]:animate-disappear"
            ),
            children: [
              searchable && /* @__PURE__ */ jsx(
                "div",
                {
                  className: clsx4(
                    "sticky top-0 z-10",
                    "bg-white dark:bg-zinc-900",
                    "border-b border-zinc-200 dark:border-zinc-700",
                    "p-2"
                  ),
                  children: /* @__PURE__ */ jsx(
                    "input",
                    {
                      type: "text",
                      value: searchQuery,
                      onChange: (event) => setSearchQuery(event.target.value),
                      onKeyDown: handleSearchKeyDown,
                      placeholder: "Rechercher...",
                      "aria-label": "Rechercher dans la liste",
                      className: clsx4(
                        "w-full rounded-md border border-zinc-300 dark:border-zinc-700",
                        "bg-white dark:bg-zinc-900",
                        "px-2 py-1 text-sm text-zinc-900 dark:text-zinc-100",
                        "placeholder:text-zinc-500 dark:placeholder:text-zinc-400",
                        "focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      )
                    }
                  )
                }
              ),
              groupBy ? visibleCollection.group().map(([groupName, groupItems]) => /* @__PURE__ */ jsxs(Select$1.ItemGroup, { children: [
                /* @__PURE__ */ jsx(
                  Select$1.ItemGroupLabel,
                  {
                    className: clsx4(
                      "px-3 py-2 text-xs font-semibold text-zinc-500 dark:text-zinc-400",
                      "bg-zinc-50 dark:bg-zinc-800",
                      "sticky top-0"
                    ),
                    children: groupName
                  }
                ),
                groupItems.map((item) => renderItem(item))
              ] }, groupName)) : visibleCollection.items.map((item) => renderItem(item)),
              searchable && normalizedQuery && filteredOptions.length === 0 && /* @__PURE__ */ jsx("div", { className: "px-3 py-2 text-sm text-zinc-500 dark:text-zinc-400", children: "Aucun r\xE9sultat" })
            ]
          }
        ) }) }),
        /* @__PURE__ */ jsx(Select$1.HiddenSelect, {})
      ]
    }
  );
}
Select.displayName = "Select";
var Root4 = ({
  children,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(Steps$1.Root, { className: clsx4(className), ...props, children });
};
Root4.displayName = "Steps.Root";
var List = ({
  children,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    Steps$1.List,
    {
      className: clsx4("mb-6 flex items-center gap-1", className),
      ...props,
      children
    }
  );
};
List.displayName = "Steps.List";
var Item2 = ({
  children,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    Steps$1.Item,
    {
      className: clsx4("flex items-center gap-1.5", className),
      ...props,
      children
    }
  );
};
Item2.displayName = "Steps.Item";
var Trigger4 = ({
  children,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    Steps$1.Trigger,
    {
      className: clsx4("flex items-center gap-1.5 text-xs", className),
      ...props,
      children
    }
  );
};
Trigger4.displayName = "Steps.Trigger";
var Indicator = ({
  children,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    Steps$1.Indicator,
    {
      className: clsx4(
        "flex size-6 shrink-0 items-center justify-center rounded-full border-2 text-xs font-medium",
        "border-zinc-300 text-zinc-500",
        "data-[current]:border-blue-500 data-[current]:bg-blue-500 data-[current]:text-white",
        "data-[complete]:border-green-500 data-[complete]:bg-green-500 data-[complete]:text-white",
        "dark:border-zinc-600 dark:text-zinc-400",
        className
      ),
      ...props,
      children
    }
  );
};
Indicator.displayName = "Steps.Indicator";
var Separator = ({
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(
    Steps$1.Separator,
    {
      className: clsx4(
        "mx-1 h-px w-4 shrink-0",
        "bg-zinc-300 dark:bg-zinc-600",
        "data-[complete]:bg-green-500",
        className
      ),
      ...props
    }
  );
};
Separator.displayName = "Steps.Separator";
var Content4 = ({
  children,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(Steps$1.Content, { className: clsx4(className), ...props, children });
};
Content4.displayName = "Steps.Content";
var CompletedContent = ({
  children,
  className,
  ...props
}) => {
  return /* @__PURE__ */ jsx(Steps$1.CompletedContent, { className: clsx4(className), ...props, children });
};
CompletedContent.displayName = "Steps.CompletedContent";
var Steps = {
  Root: Root4,
  List,
  Item: Item2,
  Trigger: Trigger4,
  Indicator,
  Separator,
  Content: Content4,
  CompletedContent
};
var TabsRoot = ({ className, ...props }) => {
  return /* @__PURE__ */ jsx(Tabs$1.Root, { className: clsx4("w-full", className), ...props });
};
var TabsList = ({ className, ...props }) => {
  return /* @__PURE__ */ jsx(
    Tabs$1.List,
    {
      className: clsx4(
        "flex gap-1 border-b border-zinc-200 data-[orientation=vertical]:flex-col data-[orientation=vertical]:border-b-0 data-[orientation=vertical]:border-r dark:border-zinc-800",
        className
      ),
      ...props
    }
  );
};
var TabsTrigger = ({ className, ...props }) => {
  return /* @__PURE__ */ jsx(
    Tabs$1.Trigger,
    {
      className: clsx4(
        "relative inline-flex items-center justify-center whitespace-nowrap px-4 py-2 text-sm font-medium text-zinc-600 transition-colors hover:text-zinc-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:text-zinc-900 data-[state=active]:shadow-sm dark:text-zinc-400 dark:hover:text-zinc-100 dark:data-[state=active]:text-zinc-100",
        className
      ),
      ...props
    }
  );
};
var TabsContent = ({ className, ...props }) => {
  return /* @__PURE__ */ jsx(
    Tabs$1.Content,
    {
      className: clsx4(
        "mt-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2",
        className
      ),
      ...props
    }
  );
};
var TabsIndicator = ({ className, ...props }) => {
  return /* @__PURE__ */ jsx(
    Tabs$1.Indicator,
    {
      className: clsx4(
        "absolute bottom-0 left-0 h-0.5 bg-blue-600 transition-all duration-200 data-[orientation=vertical]:bottom-auto data-[orientation=vertical]:right-0 data-[orientation=vertical]:top-0 data-[orientation=vertical]:h-full data-[orientation=vertical]:w-0.5 dark:bg-blue-500",
        className
      ),
      ...props
    }
  );
};
var Tabs = {
  Root: TabsRoot,
  List: TabsList,
  Trigger: TabsTrigger,
  Content: TabsContent,
  Indicator: TabsIndicator
};
var ToastContext = createContext({});
var ToastRoot = ({ children, type = "info", ...props }) => {
  return /* @__PURE__ */ jsx(ToastContext.Provider, { value: { type }, children: /* @__PURE__ */ jsxs(
    Toast$1.Root,
    {
      className: clsx4(
        "flex w-80 items-start gap-3 rounded-lg border p-4 shadow-lg",
        // Ark positions a toast through these variables and leaves applying
        // them to us: without them every toast lands on the same spot, the
        // oldest one on top of the newest.
        "[translate:var(--x)_var(--y)] [scale:var(--scale)] [z-index:var(--z-index)] [height:var(--height)] [opacity:var(--opacity)]",
        "transition-[translate,scale,opacity,height] duration-300",
        "data-[state=open]:animate-appear data-[state=closed]:animate-disappear",
        type === "info" && "border-blue-200 bg-blue-50 dark:border-blue-800 dark:bg-blue-950",
        type === "success" && "border-green-200 bg-green-50 dark:border-green-800 dark:bg-green-950",
        type === "warning" && "border-yellow-200 bg-yellow-50 dark:border-yellow-800 dark:bg-yellow-950",
        type === "error" && "border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950"
      ),
      ...props,
      children: [
        /* @__PURE__ */ jsxs("div", { className: "flex-shrink-0", children: [
          type === "info" && /* @__PURE__ */ jsx(Info, { className: "h-5 w-5 text-blue-600 dark:text-blue-400" }),
          type === "success" && /* @__PURE__ */ jsx(CheckCircle, { className: "h-5 w-5 text-green-600 dark:text-green-400" }),
          type === "warning" && /* @__PURE__ */ jsx(Warning, { className: "h-5 w-5 text-yellow-600 dark:text-yellow-400" }),
          type === "error" && /* @__PURE__ */ jsx(WarningCircle, { className: "h-5 w-5 text-red-600 dark:text-red-400" })
        ] }),
        /* @__PURE__ */ jsx("div", { className: "flex-1", children }),
        /* @__PURE__ */ jsx(
          Toast$1.CloseTrigger,
          {
            className: clsx4(
              "flex-shrink-0 rounded p-1 hover:bg-black/5 dark:hover:bg-white/10",
              type === "info" && "text-blue-600 dark:text-blue-400",
              type === "success" && "text-green-600 dark:text-green-400",
              type === "warning" && "text-yellow-600 dark:text-yellow-400",
              type === "error" && "text-red-600 dark:text-red-400"
            ),
            children: /* @__PURE__ */ jsx(X, { className: "h-4 w-4" })
          }
        )
      ]
    }
  ) });
};
ToastRoot.displayName = "Toast";
var Title3 = ({
  children,
  className,
  ...props
}) => {
  const { type } = useContext(ToastContext);
  return /* @__PURE__ */ jsx(
    Toast$1.Title,
    {
      className: clsx4(
        "font-semibold text-sm",
        type === "info" && "text-blue-900 dark:text-blue-100",
        type === "success" && "text-green-900 dark:text-green-100",
        type === "warning" && "text-yellow-900 dark:text-yellow-100",
        type === "error" && "text-red-900 dark:text-red-100",
        className
      ),
      ...props,
      children
    }
  );
};
Title3.displayName = "Toast.Title";
var Description3 = ({
  children,
  className,
  ...props
}) => {
  const { type } = useContext(ToastContext);
  return /* @__PURE__ */ jsx(
    Toast$1.Description,
    {
      className: clsx4(
        "whitespace-pre-line text-sm",
        type === "info" && "text-blue-700 dark:text-blue-300",
        type === "success" && "text-green-700 dark:text-green-300",
        type === "warning" && "text-yellow-700 dark:text-yellow-300",
        type === "error" && "text-red-700 dark:text-red-300",
        className
      ),
      ...props,
      children
    }
  );
};
Description3.displayName = "Toast.Description";
var Toast = Object.assign(ToastRoot, {
  Title: Title3,
  Description: Description3,
  ActionTrigger: Toast$1.ActionTrigger,
  CloseTrigger: Toast$1.CloseTrigger
});
function useEffectOnce(effect) {
  const hasRun = useRef(false);
  useEffect(() => {
    if (hasRun.current) {
      return;
    }
    hasRun.current = true;
    return effect();
  }, []);
}

export { Badge, Button, Checkbox, Combobox, ConfirmationDialog, DataTable, DatePicker, Drawer, GroupedTable, LoadingCell, LoadingSpinner, Menu, Modal, Pagination, PaginationEllipsis, PaginationItem, PaginationNextTrigger, PaginationPrevTrigger, Select, Slot, Steps, Table, Tabs, Toast, assignRef, clsx, cn, generateLoadingRows, mergeRefs, useEffectOnce, useMergeRefs };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map